import { ScrollX } from '@codezo/quote-detail';
import { Fragment } from 'react';
import { Input } from '../../../components/ui/input';
import { cn } from '../../../lib/utils';
import {
	shippingRowsFor,
	type PriceFormErrors,
	type ShippingDraft,
	type TierDraft,
	type VetterJobTier,
} from './price-form';

interface TierPriceTableProps {
	tiers: VetterJobTier[];
	drafts: Record<string, TierDraft>;
	currency: string;
	errors: PriceFormErrors;
	disabled: boolean;
	onChange: (tierId: string, next: TierDraft) => void;
}

const CELL = 'px-2 py-2 align-top';
const EMPTY_SHIPPING: ShippingDraft = { price: '', daysMin: '', daysMax: '' };
const HEAD = 'px-2 pb-1.5 text-left text-xs font-medium tracking-[0.06em] text-text-4 uppercase';

/**
 * One product's quantities as a table: unit price and production per row, and
 * a Shipping column listing what the supplier prices for that quantity — the
 * studio's delivery options (every part of a split on its own line) once it
 * has set them up, the plain Standard / Express for the whole quantity until
 * then. Every field is optional until one is filled — an empty row is a
 * quantity they don't quote, an empty shipping line a way they don't ship.
 */
export function TierPriceTable({
	tiers,
	drafts,
	currency,
	errors,
	disabled,
	onChange,
}: TierPriceTableProps) {
	return (
		<ScrollX>
			<table className="w-full min-w-[820px] border-collapse text-sm">
				<thead>
					<tr>
						<th className={HEAD}>Quantity</th>
						<th className={HEAD}>Unit price</th>
						<th className={HEAD}>Production</th>
						<th className={HEAD}>Shipping — your cost</th>
					</tr>
				</thead>
				<tbody>
					{tiers.map((tier) => {
						const id = tier.orderQuoteItemQuantityId;
						const draft = drafts[id];
						const rows = shippingRowsFor(tier);
						const rowErrors = [
							errors[`${id}.unitPrice`],
							errors[`${id}.days`] && `Production: ${errors[`${id}.days`]}`,
						].filter((e): e is string => !!e);

						const set = (patch: Partial<TierDraft>) =>
							onChange(id, { ...draft, ...patch });
						const setShipping = (key: string, patch: Partial<ShippingDraft>) =>
							set({
								shipping: {
									...draft.shipping,
									[key]: { ...(draft.shipping[key] ?? EMPTY_SHIPPING), ...patch },
								},
							});

						return (
							<Fragment key={id}>
								<tr className="border-t border-line-1">
									<td
										className={cn(
											CELL,
											'pt-3.5 font-mono font-medium whitespace-nowrap text-text-1'
										)}
									>
										{tier.quantity.toLocaleString('en-US')}
										<span className="ml-1 font-sans text-xs font-normal text-text-4">
											pcs
										</span>
									</td>
									<td className={CELL}>
										<MoneyField
											value={draft.unitPrice}
											onChange={(unitPrice) => set({ unitPrice })}
											currency={currency}
											invalid={!!errors[`${id}.unitPrice`]}
											disabled={disabled}
											label={`Unit price for ${tier.quantity}`}
										/>
									</td>
									<td className={CELL}>
										<DayRange
											min={draft.minDays}
											max={draft.maxDays}
											onChange={(minDays, maxDays) =>
												set({ minDays, maxDays })
											}
											invalid={!!errors[`${id}.days`]}
											disabled={disabled}
										/>
									</td>
									<td className={CELL}>
										<div className="flex flex-col gap-1.5">
											{tier.deliveryOptions.length === 0 && (
												<span className="text-xs text-text-4">
													The studio hasn&apos;t set up delivery yet —
													price the whole quantity by method. Leave one
													empty if you don&apos;t offer it.
												</span>
											)}
											{rows.map((row) => {
												// A row the studio added since the form was built starts empty.
												const value =
													draft.shipping[row.key] ?? EMPTY_SHIPPING;
												const error = errors[`${id}.ship.${row.key}`];
												return (
													<Fragment key={row.key}>
														{row.kind === 'option' &&
															row.groupLabel && (
																<span className="mt-1 text-xs font-medium text-text-3">
																	Split · {row.groupLabel}
																</span>
															)}
														<div className="flex flex-wrap items-center gap-2">
															<span
																className={cn(
																	'w-36 truncate text-sm text-text-2',
																	row.kind === 'option' &&
																		row.partId &&
																		'pl-3'
																)}
																title={row.label}
															>
																{row.label}
																{row.kind === 'method' && (
																	<span className="text-text-4">
																		{' '}
																		· whole qty
																	</span>
																)}
															</span>
															<MoneyField
																value={value.price}
																onChange={(price) =>
																	setShipping(row.key, { price })
																}
																currency={currency}
																invalid={!!error}
																disabled={disabled}
																placeholder="—"
																label={`Shipping ${row.label} for ${tier.quantity}`}
															/>
															<DayRange
																min={value.daysMin}
																max={value.daysMax}
																onChange={(daysMin, daysMax) =>
																	setShipping(row.key, {
																		daysMin,
																		daysMax,
																	})
																}
																invalid={!!error}
																disabled={disabled}
															/>
														</div>
														{error && (
															<span className="text-sm text-status-danger">
																{error}
															</span>
														)}
													</Fragment>
												);
											})}
										</div>
									</td>
								</tr>
								{rowErrors.length > 0 && (
									<tr>
										<td />
										<td
											colSpan={3}
											className="px-2 pb-2 text-sm text-status-danger"
										>
											{rowErrors.join(' · ')}
										</td>
									</tr>
								)}
							</Fragment>
						);
					})}
				</tbody>
			</table>
		</ScrollX>
	);
}

function MoneyField({
	value,
	onChange,
	currency,
	invalid,
	disabled,
	placeholder = '0.00',
	label,
}: {
	value: string;
	onChange: (value: string) => void;
	currency: string;
	invalid: boolean;
	disabled: boolean;
	placeholder?: string;
	label: string;
}) {
	return (
		<div className="relative w-[128px]">
			<Input
				inputMode="decimal"
				value={value}
				onChange={(e) => onChange(e.target.value)}
				placeholder={placeholder}
				aria-invalid={invalid}
				aria-label={label}
				disabled={disabled}
				className="pr-11 text-right font-mono"
			/>
			<span className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-xs text-text-4">
				{currency}
			</span>
		</div>
	);
}

function DayRange({
	min,
	max,
	onChange,
	invalid,
	disabled,
}: {
	min: string;
	max: string;
	onChange: (min: string, max: string) => void;
	invalid: boolean;
	disabled: boolean;
}) {
	const digits = (v: string) => v.replace(/\D/g, '');
	return (
		<div className="flex items-center gap-1">
			<Input
				inputMode="numeric"
				value={min}
				onChange={(e) => onChange(digits(e.target.value), max)}
				placeholder="min"
				aria-label="Days, from"
				aria-invalid={invalid}
				disabled={disabled}
				className="w-14 text-center"
			/>
			<span className="text-text-4">–</span>
			<Input
				inputMode="numeric"
				value={max}
				onChange={(e) => onChange(min, digits(e.target.value))}
				placeholder="max"
				aria-label="Days, to"
				aria-invalid={invalid}
				disabled={disabled}
				className="w-14 text-center"
			/>
			<span className="text-xs text-text-4">d</span>
		</div>
	);
}
